import {
  ConfirmationNumberOutlined,
  GridViewOutlined,
  LocalAtmOutlined,
  PersonOutline,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import RaffleService from '../../services/raffle';

const INITIAL_FORM = {
  lote_rifa: '',
  numero_rifa: '',
  nameSale: '',
  dni: '',
  telefono: '',
  amount: '',
  description: '',
};

const INITIAL_AVAILABILITY = {
  checked: false,
  available: false,
  data: null,
  error: '',
  lote: '',
  numero: '',
};

const getBackendErrorMessage = (error, fallback = 'Ocurrio un error inesperado') => {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (typeof data?.message === 'string' && data.message.trim()) return data.message;
  if (typeof data?.error === 'string' && data.error.trim()) return data.error;
  if (typeof data?.detail === 'string' && data.detail.trim()) return data.detail;
  if (data && typeof data === 'object') return JSON.stringify(data);
  if (typeof error?.message === 'string' && error.message.trim()) return error.message;
  return fallback;
};

const normalizeLotesResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.lotes)) return response.lotes;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const formatRaffleNumber = (availabilityData, numero) => {
  const raw = availabilityData?.numero_formateado || numero;
  if (raw === null || raw === undefined || raw === '') return '----';
  return String(raw).padStart(4, '0');
};

const getLoteMinimumAmount = (lote) => {
  const amount = Number(lote?.config?.monto_minimo || 0);
  if (!Number.isFinite(amount) || amount <= 0) return '';
  return String(amount);
};

const RaffleStandaloneSaleDialog = ({
  open,
  onClose,
  storeId,
  userId,
  cashRegisterId,
  onSuccess,
  disabled = false,
}) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [lotes, setLotes] = useState([]);
  const [loadingLotes, setLoadingLotes] = useState(false);
  const [loadingNextNumber, setLoadingNextNumber] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [creatingSale, setCreatingSale] = useState(false);
  const [availability, setAvailability] = useState(INITIAL_AVAILABILITY);
  const [generalError, setGeneralError] = useState('');
  const [successData, setSuccessData] = useState(null);
  const [boardOpen, setBoardOpen] = useState(false);
  const [boardLoading, setBoardLoading] = useState(false);
  const [boardError, setBoardError] = useState('');
  const [boardNumbers, setBoardNumbers] = useState([]);
  const [boardPage, setBoardPage] = useState(1);
  const [boardLoadedCount, setBoardLoadedCount] = useState(0);
  const [boardTotal, setBoardTotal] = useState(0);

  const availabilityRequestRef = useRef(0);

  const invalidateAvailability = () => {
    setAvailability(INITIAL_AVAILABILITY);
  };

  const handleClose = () => {
    if (creatingSale) return;
    resetFormState({ clearSuccess: true });
    onClose && onClose();
  };

  useEffect(() => {
    if (!open) return;
    setGeneralError('');
    setSuccessData(null);
    setBoardOpen(false);
    setBoardError('');
    setBoardNumbers([]);
    setBoardPage(1);
    setBoardLoadedCount(0);
    setBoardTotal(0);

    const loadLotes = async () => {
      setLoadingLotes(true);
      try {
        const response = await RaffleService.getRaffleLotes();
        const nextLotes = normalizeLotesResponse(response).filter((lote) => Boolean(lote?.activo));
        setLotes(nextLotes);

        if (nextLotes.length === 1) {
          setForm((prev) => ({
            ...prev,
            lote_rifa: String(nextLotes[0].lote),
            amount: getLoteMinimumAmount(nextLotes[0]),
          }));
        }
      } catch (error) {
        setLotes([]);
        setGeneralError(getBackendErrorMessage(error, 'No se pudieron cargar los lotes de rifa'));
      } finally {
        setLoadingLotes(false);
      }
    };

    loadLotes();
  }, [open]);

  const checkAvailability = async (lote, numero) => {
    if (!lote || !numero) return;

    const requestId = availabilityRequestRef.current + 1;
    availabilityRequestRef.current = requestId;
    setCheckingAvailability(true);

    try {
      const response = await RaffleService.getRaffleAvailability(Number(lote), Number(numero));
      if (availabilityRequestRef.current !== requestId) return;

      const available = Boolean(response?.disponible);
      setAvailability({
        checked: true,
        available,
        data: response,
        error: available ? '' : (response?.message || 'El numero no esta disponible'),
        lote: String(lote),
        numero: String(numero),
      });
    } catch (error) {
      if (availabilityRequestRef.current !== requestId) return;
      setAvailability({
        checked: true,
        available: false,
        data: null,
        error: getBackendErrorMessage(error, 'El numero no esta disponible'),
        lote: String(lote),
        numero: String(numero),
      });
    } finally {
      if (availabilityRequestRef.current === requestId) {
        setCheckingAvailability(false);
      }
    }
  };

  useEffect(() => {
    if (!open) return;
    const lote = form.lote_rifa;
    const numero = form.numero_rifa;

    if (!lote || !numero) {
      setCheckingAvailability(false);
      return;
    }

    const shouldKeepAvailability =
      availability.checked
      && availability.lote === String(lote)
      && availability.numero === String(numero);

    if (shouldKeepAvailability) {
      return;
    }

    const timer = setTimeout(() => {
      checkAvailability(lote, numero);
    }, 500);

    return () => clearTimeout(timer);
  }, [form.lote_rifa, form.numero_rifa, open, availability.checked, availability.lote, availability.numero]);

  const selectedLote = useMemo(
    () => lotes.find((l) => String(l?.lote) === String(form.lote_rifa)),
    [lotes, form.lote_rifa]
  );

  const isAvailabilityFresh =
    availability.checked
    && availability.lote === String(form.lote_rifa)
    && availability.numero === String(form.numero_rifa);

  const isAmountValid = Number(form.amount) > 0;
  const canConfirm =
    !disabled
    && !creatingSale
    && Boolean(form.lote_rifa)
    && Boolean(form.numero_rifa)
    && Boolean(form.nameSale?.trim())
    && isAmountValid
    && isAvailabilityFresh
    && availability.available;

  const handleLoteChange = (event) => {
    const nextLote = lotes.find((lote) => String(lote?.lote) === String(event.target.value));
    setGeneralError('');
    setSuccessData(null);
    setForm((prev) => ({
      ...prev,
      lote_rifa: event.target.value,
      numero_rifa: '',
      amount: getLoteMinimumAmount(nextLote),
    }));
    invalidateAvailability();
  };

  const handleNumberChange = (event) => {
    const sanitized = String(event.target.value || '').replace(/[^0-9]/g, '');
    setGeneralError('');
    setSuccessData(null);
    setForm((prev) => ({ ...prev, numero_rifa: sanitized }));
    invalidateAvailability();
  };

  const handleUseNextAvailable = async () => {
    if (!form.lote_rifa) {
      setGeneralError('Selecciona un lote antes de buscar el siguiente numero');
      return;
    }

    setGeneralError('');
    setSuccessData(null);
    setLoadingNextNumber(true);
    try {
      const response = await RaffleService.getNextRaffleNumber(Number(form.lote_rifa));
      const nextNumber = response?.numero ?? response?.number;
      if (!nextNumber && nextNumber !== 0) {
        throw new Error('No se encontro un numero disponible para el lote seleccionado');
      }

      setForm((prev) => ({ ...prev, numero_rifa: String(nextNumber) }));
      invalidateAvailability();
    } catch (error) {
      setGeneralError(getBackendErrorMessage(error, 'No se pudo obtener el siguiente numero disponible'));
    } finally {
      setLoadingNextNumber(false);
    }
  };

  const handleVerifyNow = async () => {
    if (!form.lote_rifa || !form.numero_rifa) return;
    await checkAvailability(form.lote_rifa, form.numero_rifa);
  };

  const loadBoardNumbers = async (lote, page = 1, append = false) => {
    if (!lote) return;

    setBoardLoading(true);
    setBoardError('');
    try {
      const response = await RaffleService.getLote(Number(lote), { page, limit: 100 });
      const numbers = Array.isArray(response?.numbers) ? response.numbers : [];
      const total = Number(response?.total || 0);

      setBoardNumbers((prev) => (append ? prev.concat(numbers) : numbers));
      setBoardLoadedCount((prev) => (append ? prev + numbers.length : numbers.length));
      setBoardTotal(total);
      setBoardPage(page);
    } catch (error) {
      setBoardError(getBackendErrorMessage(error, 'No se pudieron cargar los numeros del lote'));
    } finally {
      setBoardLoading(false);
    }
  };

  const handleOpenBoard = async () => {
    if (!form.lote_rifa) {
      setGeneralError('Selecciona un lote para abrir el tablero de numeros');
      return;
    }

    setBoardOpen(true);
    setBoardNumbers([]);
    setBoardPage(1);
    setBoardLoadedCount(0);
    setBoardTotal(0);
    await loadBoardNumbers(form.lote_rifa, 1, false);
  };

  const handlePickNumberFromBoard = async (numberData) => {
    const selectedNumber = numberData?.numero;
    if (selectedNumber === null || selectedNumber === undefined || numberData?.estado === 'vendido') {
      return;
    }

    const lote = form.lote_rifa;
    const numberAsText = String(selectedNumber);
    setForm((prev) => ({ ...prev, numero_rifa: numberAsText }));
    invalidateAvailability();
    setBoardOpen(false);
    await checkAvailability(lote, numberAsText);
  };

  const handleLoadMoreBoard = async () => {
    if (boardLoading || boardLoadedCount >= boardTotal) return;
    await loadBoardNumbers(form.lote_rifa, boardPage + 1, true);
  };

  const resetFormState = ({ clearSuccess = false } = {}) => {
    setForm(INITIAL_FORM);
    setGeneralError('');
    setCheckingAvailability(false);
    invalidateAvailability();
    if (clearSuccess) {
      setSuccessData(null);
    }
  };

  const handleSubmit = async () => {
    if (!canConfirm || !storeId) return;

    const payload = {
      lote_rifa: Number(form.lote_rifa),
      numero_rifa: Number(form.numero_rifa),
      nameSale: form.nameSale.trim(),
      dni: form.dni?.trim() || '',
      telefono: form.telefono?.trim() || '',
      amount: Number(form.amount),
      description: form.description?.trim() || undefined,
    };

    if (userId) {
      payload.userId = userId;
    }
    if (cashRegisterId) {
      payload.cash_register_id = cashRegisterId;
    }

    setCreatingSale(true);
    setGeneralError('');
    try {
      const response = await RaffleService.createStandaloneRaffleSale(storeId, payload);
      setSuccessData(response);
      onSuccess && onSuccess(response);
      resetFormState();
    } catch (error) {
      setGeneralError(getBackendErrorMessage(error, 'No se pudo registrar la venta de rifa'));
      if (form.lote_rifa && form.numero_rifa) {
        await checkAvailability(form.lote_rifa, form.numero_rifa);
      }
    } finally {
      setCreatingSale(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth='sm'
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1.25 }}>
        <Box display='flex' alignItems='center' gap={1}>
          <ConfirmationNumberOutlined color='warning' />
          <Typography variant='h6' fontWeight={700}>Venta de rifa</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {generalError && <Alert severity='error'>{generalError}</Alert>}

          {successData && (
            <Alert severity='success'>
              <Typography variant='body2' fontWeight={700}>Venta registrada correctamente</Typography>
              <Typography variant='caption' display='block'>
                Numero: {formatRaffleNumber(successData, successData?.numero_rifa)} | Comprador: {successData?.nameSale || '-'} | Monto: {Number(successData?.amount || 0)}
              </Typography>
              {(successData?.id_sale || successData?.movement_id) && (
                <Typography variant='caption' display='block'>
                  {successData?.id_sale ? `ID venta: ${successData.id_sale}` : ''}
                  {successData?.id_sale && successData?.movement_id ? ' | ' : ''}
                  {successData?.movement_id ? `Movimiento: ${successData.movement_id}` : ''}
                </Typography>
              )}
            </Alert>
          )}

          <TextField
            select
            fullWidth
            label='Lote'
            value={form.lote_rifa}
            onChange={handleLoteChange}
            disabled={loadingLotes || creatingSale || disabled}
            helperText={loadingLotes ? 'Cargando rifas activas...' : (lotes.length === 0 ? 'No hay rifas activas para vender' : '')}
          >
            {lotes.map((lote) => (
              <MenuItem key={String(lote?.lote)} value={String(lote?.lote)}>
                Lote {lote?.lote}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} alignItems='flex-start'>
            <TextField
              label='Numero de rifa'
              type='text'
              value={form.numero_rifa}
              onChange={handleNumberChange}
              fullWidth
              disabled={creatingSale || disabled || !form.lote_rifa}
            />
            <Button
              variant='outlined'
              onClick={handleUseNextAvailable}
              disabled={!form.lote_rifa || creatingSale || loadingNextNumber || disabled}
              sx={{ minWidth: { xs: '100%', sm: 190 }, height: 56, textTransform: 'none' }}
            >
              {loadingNextNumber ? <CircularProgress size={18} /> : 'Usar siguiente disponible'}
            </Button>
          </Stack>

          <Button
            variant='outlined'
            color='secondary'
            startIcon={<GridViewOutlined />}
            onClick={handleOpenBoard}
            disabled={!form.lote_rifa || creatingSale || disabled}
            sx={{ textTransform: 'none', width: { xs: '100%', sm: 'auto' } }}
          >
            Elegir desde tablero
          </Button>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} alignItems='center'>
            <Button
              variant='text'
              onClick={handleVerifyNow}
              disabled={!form.lote_rifa || !form.numero_rifa || checkingAvailability || creatingSale || disabled}
              sx={{ textTransform: 'none' }}
            >
              {checkingAvailability ? 'Verificando...' : 'Verificar disponibilidad'}
            </Button>
            {isAvailabilityFresh && availability.available && (
              <Chip
                color='success'
                variant='outlined'
                label={`Disponible · #${formatRaffleNumber(availability.data, form.numero_rifa)} · Lote ${form.lote_rifa}`}
              />
            )}
            {isAvailabilityFresh && availability.checked && !availability.available && (
              <Chip color='error' variant='outlined' label='No disponible' />
            )}
          </Stack>

          {isAvailabilityFresh && availability.error && (
            <Alert severity='error'>{availability.error}</Alert>
          )}

          <TextField
            label='Comprador'
            value={form.nameSale}
            onChange={(event) => {
              setGeneralError('');
              setForm((prev) => ({ ...prev, nameSale: event.target.value }));
            }}
            fullWidth
            disabled={creatingSale || disabled}
            InputProps={{
              startAdornment: <PersonOutline sx={{ color: 'text.secondary', mr: 1 }} fontSize='small' />,
            }}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
            <TextField
              label='DNI'
              value={form.dni}
              onChange={(event) => setForm((prev) => ({ ...prev, dni: event.target.value }))}
              fullWidth
              disabled={creatingSale || disabled}
            />
            <TextField
              label='Telefono'
              value={form.telefono}
              onChange={(event) => setForm((prev) => ({ ...prev, telefono: event.target.value }))}
              fullWidth
              disabled={creatingSale || disabled}
            />
          </Stack>

          <TextField
            label='Monto'
            type='number'
            value={form.amount}
            onChange={(event) => setForm((prev) => ({ ...prev, amount: event.target.value }))}
            fullWidth
            disabled={creatingSale || disabled}
            inputProps={{ min: 1, step: 1 }}
            error={form.amount !== '' && !isAmountValid}
            helperText={form.amount !== '' && !isAmountValid ? 'El monto debe ser mayor a 0' : ''}
            InputProps={{
              startAdornment: <LocalAtmOutlined sx={{ color: 'text.secondary', mr: 1 }} fontSize='small' />,
            }}
          />

          <TextField
            label='Descripcion (opcional)'
            value={form.description}
            onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
            fullWidth
            multiline
            minRows={2}
            disabled={creatingSale || disabled}
          />

          {selectedLote?.config?.monto_minimo ? (
            <Typography variant='caption' color='text.secondary'>
              Referencia lote: monto minimo sugerido ${selectedLote.config.monto_minimo}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={creatingSale}>Cancelar</Button>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={!canConfirm}
          sx={{ textTransform: 'none', minWidth: 180 }}
        >
          {creatingSale ? <CircularProgress size={18} color='inherit' /> : 'Confirmar venta de rifa'}
        </Button>
      </DialogActions>

      <Dialog
        open={boardOpen}
        onClose={() => setBoardOpen(false)}
        fullWidth
        maxWidth='md'
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display='flex' alignItems='center' justifyContent='space-between' gap={1}>
            <Box display='flex' alignItems='center' gap={1}>
              <GridViewOutlined color='secondary' />
              <Typography variant='h6' fontWeight={700}>Tablero de numeros</Typography>
            </Box>
            <Tooltip title='Cerrar'>
              <IconButton size='small' onClick={() => setBoardOpen(false)}>
                <ConfirmationNumberOutlined fontSize='small' />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant='caption' color='text.secondary'>
            Lote {form.lote_rifa || '-'} · Tocá un numero disponible para seleccionarlo
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.2}>
            {boardError && <Alert severity='error'>{boardError}</Alert>}
            {boardLoading && boardNumbers.length === 0 && (
              <Box display='flex' justifyContent='center' py={2}>
                <CircularProgress size={26} />
              </Box>
            )}

            <Stack direction='row' spacing={1} flexWrap='wrap'>
              <Chip label='Disponible' color='success' size='small' variant='outlined' />
              <Chip label='Vendido' color='error' size='small' variant='outlined' />
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(78px, 1fr))',
                gap: 0.9,
              }}
            >
              {boardNumbers.map((n) => {
                const isSold = n?.estado === 'vendido';
                const selected = String(n?.numero) === String(form.numero_rifa);
                const label = n?.numero_formateado || String(n?.numero || '').padStart(4, '0');

                return (
                  <Tooltip
                    key={`${n?.numero}-${n?._id || 'n'}`}
                    title={isSold ? `Vendido${n?.nombre_comprador ? ` · ${n.nombre_comprador}` : ''}` : 'Disponible'}
                  >
                    <Box>
                      <Button
                        fullWidth
                        size='small'
                        onClick={() => handlePickNumberFromBoard(n)}
                        disabled={isSold}
                        variant={selected ? 'contained' : 'outlined'}
                        color={isSold ? 'error' : (selected ? 'success' : 'inherit')}
                        sx={{
                          borderRadius: 1.6,
                          minHeight: 36,
                          fontWeight: selected ? 800 : 600,
                          textTransform: 'none',
                          fontSize: '0.78rem',
                          opacity: isSold ? 0.7 : 1,
                        }}
                      >
                        {label}
                      </Button>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>

            {boardNumbers.length > 0 && boardLoadedCount < boardTotal && (
              <Box display='flex' justifyContent='center' pt={1}>
                <Button
                  onClick={handleLoadMoreBoard}
                  disabled={boardLoading}
                  variant='text'
                  sx={{ textTransform: 'none' }}
                >
                  {boardLoading ? <CircularProgress size={17} /> : `Cargar mas (${boardLoadedCount}/${boardTotal})`}
                </Button>
              </Box>
            )}

            {!boardLoading && boardNumbers.length === 0 && !boardError && (
              <Typography variant='body2' color='text.secondary' textAlign='center' py={1.5}>
                No hay numeros para mostrar en este lote
              </Typography>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default RaffleStandaloneSaleDialog;